import React, { useState, useEffect, useCallback } from "react";
import api from "../../utils/api";
import {
  Key, Webhook, Link2, Server, Globe, Activity, Plus, Trash2,
  RefreshCw, CheckCircle, XCircle, AlertCircle, Copy, Eye, EyeOff,
  ChevronRight, Wifi, WifiOff, TestTube
} from "lucide-react";

// ── Helpers ───────────────────────────────────────────────────────────────────

const STATUS_COLORS = {
  active:   "bg-green-100 text-green-800",
  testing:  "bg-yellow-100 text-yellow-800",
  inactive: "bg-gray-100 text-gray-700",
  success:  "bg-green-100 text-green-800",
  failed:   "bg-red-100 text-red-800",
  pending:  "bg-blue-100 text-blue-800",
};

function StatusBadge({ status }) {
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${STATUS_COLORS[status] || "bg-gray-100 text-gray-600"}`}>
      {status}
    </span>
  );
}

function CopyButton({ text }) {
  const [copied, setCopied] = useState(false);
  const copy = () => {
    navigator.clipboard.writeText(text).then(() => { setCopied(true); setTimeout(() => setCopied(false), 2000); });
  };
  return (
    <button onClick={copy} className="text-gray-400 hover:text-gray-600 ml-2">
      {copied ? <CheckCircle size={14} className="text-green-500" /> : <Copy size={14} />}
    </button>
  );
}

function SectionCard({ title, icon: Icon, children, action }) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
        <div className="flex items-center gap-2">
          <Icon size={18} className="text-violet-600" />
          <h3 className="font-semibold text-gray-800">{title}</h3>
        </div>
        {action}
      </div>
      <div className="p-5">{children}</div>
    </div>
  );
}

// ── API Keys Tab ──────────────────────────────────────────────────────────────

function ApiKeysTab() {
  const [keys, setKeys] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ label: "", scopes: [] });
  const [newKey, setNewKey] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const SCOPE_OPTIONS = ["asn:write", "asn:read", "po:read", "inventory:read", "*"];

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/integrations/api-keys");
      setKeys(data.keys || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.label.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/api/integrations/api-keys", form);
      setNewKey(data.apiKey);
      setShowForm(false);
      setForm({ label: "", scopes: [] });
      load();
    } catch (e) {
      alert("Failed to create key: " + (e.response?.data?.error || e.message));
    } finally { setCreating(false); }
  };

  const revoke = async (keyId) => {
    if (!window.confirm("Revoke this API key? This cannot be undone.")) return;
    await api.delete(`/api/integrations/api-keys/${keyId}`);
    load();
  };

  const toggleScope = (scope) => {
    setForm(f => ({
      ...f,
      scopes: f.scopes.includes(scope) ? f.scopes.filter(s => s !== scope) : [...f.scopes, scope]
    }));
  };

  return (
    <div className="space-y-4">
      {newKey && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-green-800 mb-1">API key created — copy it now, it won't be shown again.</p>
          <div className="flex items-center gap-2 font-mono text-sm bg-white border border-green-200 rounded px-3 py-2">
            <span className="flex-1 truncate">{newKey}</span>
            <CopyButton text={newKey} />
          </div>
          <button onClick={() => setNewKey(null)} className="mt-2 text-xs text-green-700 underline">Dismiss</button>
        </div>
      )}

      <SectionCard
        title="API Keys"
        icon={Key}
        action={
          <button
            onClick={() => setShowForm(v => !v)}
            className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 font-medium"
          >
            <Plus size={15} /> New Key
          </button>
        }
      >
        {showForm && (
          <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="Key label (e.g. Partner ABC)"
              value={form.label}
              onChange={e => setForm(f => ({ ...f, label: e.target.value }))}
            />
            <div>
              <p className="text-xs text-gray-500 mb-1 font-medium">Scopes</p>
              <div className="flex flex-wrap gap-2">
                {SCOPE_OPTIONS.map(s => (
                  <button
                    key={s}
                    onClick={() => toggleScope(s)}
                    className={`text-xs px-2 py-1 rounded border ${form.scopes.includes(s) ? "bg-violet-600 text-white border-violet-600" : "bg-white text-gray-600 border-gray-300"}`}
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={create} disabled={creating}
                className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700 disabled:opacity-50"
              >
                {creating ? "Creating…" : "Create Key"}
              </button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-sm text-gray-400">Loading…</div>
        ) : keys.length === 0 ? (
          <p className="text-sm text-gray-400">No API keys yet. Create one to enable machine-to-machine access.</p>
        ) : (
          <div className="space-y-2">
            {keys.map(k => (
              <div key={k.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-800">{k.label}</p>
                  <p className="text-xs text-gray-400 font-mono">{k.prefix}… · {k.scopes?.join(", ") || "no scopes"}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusBadge status={k.status} />
                  <button onClick={() => revoke(k.id)} className="text-red-400 hover:text-red-600">
                    <Trash2 size={15} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </SectionCard>
    </div>
  );
}

// ── Webhooks Tab ──────────────────────────────────────────────────────────────

function WebhooksTab() {
  const [endpoints, setEndpoints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ url: "", events: ["*"], direction: "outbound", description: "" });
  const [newSecret, setNewSecret] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/integrations/webhooks");
      setEndpoints(data.endpoints || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!form.url.startsWith("https://")) return alert("URL must start with https://");
    try {
      const { data } = await api.post("/api/integrations/webhooks", form);
      setNewSecret(data.secret);
      setShowForm(false);
      setForm({ url: "", events: ["*"], direction: "outbound", description: "" });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this webhook endpoint?")) return;
    await api.delete(`/api/integrations/webhooks/${id}`);
    load();
  };

  const updateStatus = async (id, status) => {
    await api.put(`/api/integrations/webhooks/${id}`, { status });
    load();
  };

  return (
    <div className="space-y-4">
      {newSecret && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4">
          <p className="text-sm font-semibold text-green-800 mb-1">Webhook secret — copy now, not shown again.</p>
          <div className="flex items-center gap-2 font-mono text-sm bg-white border border-green-200 rounded px-3 py-2">
            <span className="flex-1 truncate">{newSecret}</span>
            <CopyButton text={newSecret} />
          </div>
          <button onClick={() => setNewSecret(null)} className="mt-2 text-xs text-green-700 underline">Dismiss</button>
        </div>
      )}

      <SectionCard
        title="Webhook Endpoints"
        icon={Webhook}
        action={
          <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 font-medium">
            <Plus size={15} /> Add Endpoint
          </button>
        }
      >
        {showForm && (
          <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
            <input
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
              placeholder="https://partner.com/webhook"
              value={form.url}
              onChange={e => setForm(f => ({ ...f, url: e.target.value }))}
            />
            <div className="flex gap-3">
              <select
                className="border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={form.direction}
                onChange={e => setForm(f => ({ ...f, direction: e.target.value }))}
              >
                <option value="outbound">Outbound (TG → Partner)</option>
                <option value="inbound">Inbound (Partner → TG)</option>
              </select>
              <input
                className="flex-1 border border-gray-300 rounded-lg px-3 py-2 text-sm"
                placeholder="Events (e.g. * or asn.submitted)"
                value={form.events.join(",")}
                onChange={e => setForm(f => ({ ...f, events: e.target.value.split(",").map(s => s.trim()) }))}
              />
            </div>
            <div className="flex gap-2">
              <button onClick={create} className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700">Add</button>
              <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
            </div>
          </div>
        )}

        {loading ? <div className="text-sm text-gray-400">Loading…</div> :
          endpoints.length === 0 ? <p className="text-sm text-gray-400">No webhook endpoints configured.</p> : (
            <div className="space-y-2">
              {endpoints.map(ep => (
                <div key={ep.id} className="flex items-start justify-between py-2 border-b border-gray-100 last:border-0">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-mono text-gray-800 truncate">{ep.url}</p>
                    <p className="text-xs text-gray-400">{ep.direction} · events: {ep.events?.join(", ")}</p>
                  </div>
                  <div className="flex items-center gap-2 ml-3">
                    <StatusBadge status={ep.status} />
                    <button
                      onClick={() => updateStatus(ep.id, ep.status === "active" ? "inactive" : "active")}
                      className="text-xs text-gray-500 hover:text-gray-800"
                    >
                      {ep.status === "active" ? <WifiOff size={14} /> : <Wifi size={14} />}
                    </button>
                    <button onClick={() => del(ep.id)} className="text-red-400 hover:text-red-600"><Trash2 size={14} /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
      </SectionCard>
    </div>
  );
}

// ── AS2 Tab ───────────────────────────────────────────────────────────────────

function AS2Tab() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [testing, setTesting] = useState(null);
  const [testResult, setTestResult] = useState({});
  const [form, setForm] = useState({
    partnerName: "", partnerAs2Identifier: "", endpointUrl: "",
    signingEnabled: false, encryptionEnabled: false, status: "testing", description: ""
  });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/integrations/as2");
      setConnections(data.connections || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.partnerName || !form.partnerAs2Identifier) return alert("Partner name and AS2 ID required");
    try {
      await api.post("/api/integrations/as2", form);
      setShowForm(false);
      setForm({ partnerName: "", partnerAs2Identifier: "", endpointUrl: "", signingEnabled: false, encryptionEnabled: false, status: "testing", description: "" });
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const testConn = async (id) => {
    setTesting(id);
    try {
      const { data } = await api.post(`/api/integrations/as2/${id}/test`);
      setTestResult(r => ({ ...r, [id]: data }));
    } catch (e) {
      setTestResult(r => ({ ...r, [id]: { success: false, error: e.response?.data?.error || e.message } }));
    } finally { setTesting(null); }
  };

  return (
    <SectionCard
      title="AS2 Connections"
      icon={Server}
      action={
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 font-medium">
          <Plus size={15} /> Add Connection
        </button>
      }
    >
      <div className="mb-3 flex items-center gap-2 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
        <AlertCircle size={16} className="text-yellow-600 flex-shrink-0" />
        <p className="text-xs text-yellow-800">AS2 is in <strong>testing mode</strong>. Messages are simulated — no real transmission until you set a connection to <em>active</em> with certificates configured.</p>
      </div>

      {showForm && (
        <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Partner name" value={form.partnerName} onChange={e => setForm(f => ({ ...f, partnerName: e.target.value }))} />
            <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Partner AS2 ID" value={form.partnerAs2Identifier} onChange={e => setForm(f => ({ ...f, partnerAs2Identifier: e.target.value }))} />
          </div>
          <input className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Endpoint URL (https://)" value={form.endpointUrl} onChange={e => setForm(f => ({ ...f, endpointUrl: e.target.value }))} />
          <div className="flex gap-4 text-sm">
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.signingEnabled} onChange={e => setForm(f => ({ ...f, signingEnabled: e.target.checked }))} />
              Message signing
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input type="checkbox" checked={form.encryptionEnabled} onChange={e => setForm(f => ({ ...f, encryptionEnabled: e.target.checked }))} />
              Encryption
            </label>
            <select className="border border-gray-300 rounded px-2 py-1 text-sm" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option value="testing">Testing</option>
              <option value="inactive">Inactive</option>
              <option value="active">Active</option>
            </select>
          </div>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-sm text-gray-400">Loading…</div> :
        connections.length === 0 ? <p className="text-sm text-gray-400">No AS2 connections yet.</p> : (
          <div className="space-y-3">
            {connections.map(c => (
              <div key={c.id} className="p-3 border border-gray-200 rounded-lg">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium text-sm text-gray-800">{c.partnerName}</p>
                    <p className="text-xs text-gray-400 font-mono">AS2-ID: {c.partnerAs2Identifier}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <StatusBadge status={c.status} />
                    <button
                      onClick={() => testConn(c.id)}
                      disabled={testing === c.id}
                      className="flex items-center gap-1 text-xs px-2 py-1 border border-gray-300 rounded hover:bg-gray-50 text-gray-600 disabled:opacity-50"
                    >
                      <TestTube size={12} />
                      {testing === c.id ? "Testing…" : "Test"}
                    </button>
                  </div>
                </div>
                {testResult[c.id] && (
                  <div className={`mt-2 text-xs px-2 py-1 rounded ${testResult[c.id].success ? "bg-green-50 text-green-700" : "bg-red-50 text-red-700"}`}>
                    {testResult[c.id].success ? `✓ Test passed${testResult[c.id].testingMode ? " (simulation)" : ""}` : `✗ ${testResult[c.id].error}`}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
    </SectionCard>
  );
}

// ── VAN Tab ───────────────────────────────────────────────────────────────────

const VAN_PROVIDERS = [
  { value: "opentext",     label: "OpenText Trading Grid" },
  { value: "sps",          label: "SPS Commerce" },
  { value: "truecommerce", label: "TrueCommerce" },
  { value: "sftp",         label: "Generic SFTP" },
];

function VANTab() {
  const [connections, setConnections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ provider: "sftp", partnerName: "", host: "", port: "22", username: "", password: "", inboundPath: "/inbound", outboundPath: "/outbound", apiKey: "", accountId: "", environment: "sandbox", tradingPartnerId: "", status: "testing", description: "" });

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/api/integrations/van");
      setConnections(data.connections || []);
    } catch { } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.provider) return;
    try {
      await api.post("/api/integrations/van", form);
      setShowForm(false);
      load();
    } catch (e) { alert(e.response?.data?.error || e.message); }
  };

  const isSFTP = ["sftp", "opentext"].includes(form.provider);
  const isREST = ["sps", "truecommerce"].includes(form.provider);

  return (
    <SectionCard
      title="VAN Connections"
      icon={Globe}
      action={
        <button onClick={() => setShowForm(v => !v)} className="flex items-center gap-1 text-sm text-violet-600 hover:text-violet-800 font-medium">
          <Plus size={15} /> Add VAN
        </button>
      }
    >
      {showForm && (
        <div className="mb-5 p-4 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.provider} onChange={e => setForm(f => ({ ...f, provider: e.target.value }))}>
              {VAN_PROVIDERS.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
            </select>
            <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Partner name" value={form.partnerName} onChange={e => setForm(f => ({ ...f, partnerName: e.target.value }))} />
          </div>
          {isSFTP && (
            <>
              <div className="grid grid-cols-3 gap-3">
                <input className="col-span-2 border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="SFTP host" value={form.host} onChange={e => setForm(f => ({ ...f, host: e.target.value }))} />
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Port (22)" value={form.port} onChange={e => setForm(f => ({ ...f, port: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Username" value={form.username} onChange={e => setForm(f => ({ ...f, username: e.target.value }))} />
                <input type="password" className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Password" value={form.password} onChange={e => setForm(f => ({ ...f, password: e.target.value }))} />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Inbound path" value={form.inboundPath} onChange={e => setForm(f => ({ ...f, inboundPath: e.target.value }))} />
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="Outbound path" value={form.outboundPath} onChange={e => setForm(f => ({ ...f, outboundPath: e.target.value }))} />
              </div>
            </>
          )}
          {isREST && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder="API Key" value={form.apiKey} onChange={e => setForm(f => ({ ...f, apiKey: e.target.value }))} />
                <input className="border border-gray-300 rounded-lg px-3 py-2 text-sm" placeholder={form.provider === "truecommerce" ? "Account ID" : "Trading Partner ID"} value={form.accountId || form.tradingPartnerId} onChange={e => setForm(f => ({ ...f, accountId: e.target.value, tradingPartnerId: e.target.value }))} />
              </div>
              <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.environment} onChange={e => setForm(f => ({ ...f, environment: e.target.value }))}>
                <option value="sandbox">Sandbox</option>
                <option value="production">Production</option>
              </select>
            </>
          )}
          <select className="border border-gray-300 rounded-lg px-3 py-2 text-sm" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
            <option value="testing">Testing</option>
            <option value="inactive">Inactive</option>
            <option value="active">Active</option>
          </select>
          <div className="flex gap-2">
            <button onClick={save} className="px-4 py-2 bg-violet-600 text-white text-sm rounded-lg hover:bg-violet-700">Save</button>
            <button onClick={() => setShowForm(false)} className="px-4 py-2 text-sm text-gray-600">Cancel</button>
          </div>
        </div>
      )}

      {loading ? <div className="text-sm text-gray-400">Loading…</div> :
        connections.length === 0 ? <p className="text-sm text-gray-400">No VAN connections configured.</p> : (
          <div className="space-y-2">
            {connections.map(c => (
              <div key={c.id} className="flex items-center justify-between py-2 border-b border-gray-100 last:border-0">
                <div>
                  <p className="text-sm font-medium text-gray-800">{c.partnerName || c.provider}</p>
                  <p className="text-xs text-gray-400">{VAN_PROVIDERS.find(p => p.value === c.provider)?.label || c.provider} · {c.host || c.environment || ""}</p>
                </div>
                <StatusBadge status={c.status} />
              </div>
            ))}
          </div>
        )}
    </SectionCard>
  );
}

// ── Logs Tab ──────────────────────────────────────────────────────────────────

function LogsTab() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [source, setSource] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = source ? `?source=${source}&limit=100` : "?limit=100";
      const { data } = await api.get(`/api/integrations/logs${params}`);
      setLogs(data.logs || []);
    } catch { } finally { setLoading(false); }
  }, [source]);

  useEffect(() => { load(); }, [load]);

  const fmt = (ts) => {
    if (!ts) return "";
    const d = ts._seconds ? new Date(ts._seconds * 1000) : new Date(ts);
    return d.toLocaleString();
  };

  return (
    <SectionCard
      title="Integration Logs"
      icon={Activity}
      action={
        <div className="flex items-center gap-2">
          <select
            className="border border-gray-300 rounded px-2 py-1 text-xs"
            value={source}
            onChange={e => setSource(e.target.value)}
          >
            <option value="">All sources</option>
            <option value="as2">AS2</option>
            <option value="van">VAN</option>
            <option value="webhook">Webhook</option>
            <option value="external_api">External API</option>
            <option value="edi">EDI</option>
          </select>
          <button onClick={load} className="text-gray-400 hover:text-gray-600"><RefreshCw size={14} /></button>
        </div>
      }
    >
      {loading ? <div className="text-sm text-gray-400">Loading…</div> :
        logs.length === 0 ? <p className="text-sm text-gray-400">No logs yet.</p> : (
          <div className="space-y-1 max-h-96 overflow-y-auto">
            {logs.map((l, i) => (
              <div key={l.id || i} className="flex items-start gap-3 py-2 border-b border-gray-100 last:border-0 text-xs">
                <span className={`mt-0.5 flex-shrink-0 w-2 h-2 rounded-full ${l.status === "success" ? "bg-green-400" : l.status === "failed" ? "bg-red-400" : "bg-yellow-400"}`} />
                <div className="flex-1 min-w-0">
                  <span className="font-medium text-gray-700">{l.source} · {l.event}</span>
                  <span className="ml-2 text-gray-400">{l.direction}</span>
                  <p className="text-gray-500 truncate">{l.message}</p>
                  {l.error && <p className="text-red-500 truncate">{l.error}</p>}
                </div>
                <span className="text-gray-400 flex-shrink-0 whitespace-nowrap">{fmt(l.createdAt)}</span>
              </div>
            ))}
          </div>
        )}
    </SectionCard>
  );
}

// ── Overview Tab ──────────────────────────────────────────────────────────────

function OverviewTab({ onGoTo }) {
  const cards = [
    { icon: Key,     label: "API Keys",    desc: "Machine-to-machine REST access",   tab: "api-keys"  },
    { icon: Webhook, label: "Webhooks",    desc: "Event-driven partner notifications", tab: "webhooks" },
    { icon: Server,  label: "AS2",         desc: "AS2 transport layer (testing mode)", tab: "as2"      },
    { icon: Globe,   label: "VAN",         desc: "OpenText, SPS, TrueCommerce, SFTP",  tab: "van"      },
    { icon: Activity,label: "Logs",        desc: "Centralised integration audit trail", tab: "logs"     },
  ];

  return (
    <div className="grid grid-cols-2 gap-4">
      {cards.map(c => (
        <button
          key={c.tab}
          onClick={() => onGoTo(c.tab)}
          className="flex items-start gap-3 p-4 bg-white border border-gray-200 rounded-xl hover:border-violet-300 hover:shadow-sm text-left transition-all"
        >
          <div className="p-2 bg-violet-50 rounded-lg">
            <c.icon size={20} className="text-violet-600" />
          </div>
          <div className="flex-1">
            <p className="font-semibold text-gray-800 text-sm">{c.label}</p>
            <p className="text-xs text-gray-500">{c.desc}</p>
          </div>
          <ChevronRight size={16} className="text-gray-300 mt-1" />
        </button>
      ))}
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

const TABS = [
  { id: "overview",  label: "Overview",  icon: Link2    },
  { id: "api-keys",  label: "API Keys",  icon: Key      },
  { id: "webhooks",  label: "Webhooks",  icon: Webhook  },
  { id: "as2",       label: "AS2",       icon: Server   },
  { id: "van",       label: "VAN",       icon: Globe    },
  { id: "logs",      label: "Logs",      icon: Activity },
];

export default function IntegrationHub() {
  const [activeTab, setActiveTab] = useState("overview");

  return (
    <div className="p-6 max-w-5xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Integration Hub</h1>
        <p className="text-sm text-gray-500 mt-1">Manage EDI network connections, API keys, webhooks, and transport layers.</p>
      </div>

      {/* Tab bar */}
      <div className="flex gap-1 mb-6 bg-gray-100 rounded-xl p-1 overflow-x-auto">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-all
              ${activeTab === t.id ? "bg-white text-violet-700 shadow-sm" : "text-gray-600 hover:text-gray-800"}`}
          >
            <t.icon size={15} />
            {t.label}
          </button>
        ))}
      </div>

      {/* Tab content */}
      {activeTab === "overview"  && <OverviewTab onGoTo={setActiveTab} />}
      {activeTab === "api-keys"  && <ApiKeysTab />}
      {activeTab === "webhooks"  && <WebhooksTab />}
      {activeTab === "as2"       && <AS2Tab />}
      {activeTab === "van"       && <VANTab />}
      {activeTab === "logs"      && <LogsTab />}
    </div>
  );
}

// Tab panels, reused by the Hasbro "Partners & Vendors" page
export { ApiKeysTab, WebhooksTab, AS2Tab, VANTab, LogsTab };
